import type { NotificationDto } from "@workspace/dto/notification/schema";
import {
  renderDescription as buildDescription,
  renderTitle as buildTitle,
  getIcon,
} from "@workspace/notification-service/handlers";
import type { NotificationIcon } from "@workspace/notification-service/handlers/types";
import { Button } from "@workspace/ui/components/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@workspace/ui/components/empty";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@workspace/ui/components/popover";
import { ScrollArea } from "@workspace/ui/components/scroll-area";
import { Skeleton } from "@workspace/ui/components/skeleton";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@workspace/ui/components/tabs";
import { cn } from "@workspace/ui/lib/utils";
import { formatDistanceToNow } from "date-fns";
import {
  Archive,
  Bell,
  BriefcaseBusiness,
  type LucideIcon,
  MessageSquare,
  Settings,
  UserCheck,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState } from "react";
import { useNotifications } from "@/components/providers/message-notification-provider";
import { accountPath } from "@/lib/account-navigation";
import { useStableSession } from "@/lib/auth/use-stable-session";

const NOTIFICATION_ICONS = {
  message: MessageSquare,
  interview: UserCheck,
  application: BriefcaseBusiness,
} satisfies Record<NotificationIcon, LucideIcon>;

function NotificationTypeIcon({
  notification,
}: {
  notification: NotificationDto;
}) {
  const IconComponent =
    NOTIFICATION_ICONS[getIcon(notification.type)] ?? BriefcaseBusiness;
  return <IconComponent className="size-4" />;
}

function EmptyNotifications({ archived }: { archived?: boolean }) {
  return (
    <Empty className="h-[320px] border-0">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          {archived ? <Archive /> : <Bell />}
        </EmptyMedia>
        <EmptyTitle>
          {archived ? "No archived items" : "No notifications"}
        </EmptyTitle>
        <EmptyDescription>
          {archived
            ? "Archived notifications will appear here."
            : "Important hiring updates will appear here."}
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function NotificationTitle({
  notification,
}: {
  notification: NotificationDto;
}) {
  return <>{buildTitle(notification.type, notification.metadata)}</>;
}

function NotificationDescription({
  notification,
}: {
  notification: NotificationDto;
}) {
  return <>{buildDescription(notification.type, notification.metadata)}</>;
}

function NotificationItem({
  notification,
  onArchive,
  onNavigate,
  showArchive,
}: {
  notification: NotificationDto;
  onArchive?: (id: string) => void;
  onNavigate: (notification: NotificationDto) => void;
  showArchive?: boolean;
}) {
  const isUnread = notification.status === "unread";

  return (
    <div
      className={cn(
        "flex items-start text-left transition-colors hover:bg-muted/60",
        isUnread && "bg-muted/35"
      )}
    >
      <button
        className="flex min-w-0 flex-1 items-start gap-3 px-3 py-3 text-left"
        onClick={() => onNavigate(notification)}
        type="button"
      >
        <div className="flex size-9 shrink-0 items-center justify-center rounded-full border bg-background text-muted-foreground">
          <NotificationTypeIcon notification={notification} />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-start gap-2">
            <p
              className={cn(
                "min-w-0 flex-1 text-sm leading-snug",
                isUnread && "font-medium"
              )}
            >
              <NotificationTitle notification={notification} />
            </p>
            {isUnread && (
              <span className="mt-1 size-1.5 rounded-full bg-primary" />
            )}
          </div>
          <p className="line-clamp-2 text-muted-foreground text-xs">
            <NotificationDescription notification={notification} />
          </p>
          <p className="text-muted-foreground/80 text-xs">
            {formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
            })}
          </p>
        </div>
      </button>
      {showArchive && onArchive ? (
        <Button
          aria-label="Archive notification"
          className="mt-2 mr-3 size-8 shrink-0 rounded-full"
          onClick={(event) => {
            event.stopPropagation();
            onArchive(notification.id);
          }}
          size="icon"
          title="Archive notification"
          type="button"
          variant="ghost"
        >
          <Archive className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

function NotificationListSkeleton({ showArchive }: { showArchive?: boolean }) {
  return (
    <ScrollArea className="h-[380px]">
      <div className="divide-y">
        {[...new Array(4)].map((_, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows
          <div className="flex items-start gap-3 px-3 py-3" key={index}>
            <Skeleton className="size-9 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-1/4" />
            </div>
            {showArchive ? (
              <Skeleton className="mt-2 mr-3 size-8 shrink-0 rounded-full" />
            ) : null}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

function InboxNotifications({
  archiveAllNotifications,
  archiveNotification,
  isLoading,
  notifications,
  onNavigate,
}: {
  archiveAllNotifications: () => void;
  archiveNotification: (id: string) => void;
  isLoading: boolean;
  notifications: NotificationDto[];
  onNavigate: (notification: NotificationDto) => void;
}) {
  if (isLoading) {
    return <NotificationListSkeleton showArchive />;
  }

  if (notifications.length === 0) {
    return <EmptyNotifications />;
  }

  return (
    <>
      <ScrollArea className="h-[380px]">
        <div className="divide-y">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onArchive={archiveNotification}
              onNavigate={onNavigate}
              showArchive
            />
          ))}
        </div>
      </ScrollArea>
      <div className="flex items-center justify-between border-t px-3 py-2">
        <p className="text-muted-foreground text-xs">
          {notifications.length} {notifications.length === 1 ? "item" : "items"}
        </p>
        <Button
          onClick={archiveAllNotifications}
          size="sm"
          type="button"
          variant="ghost"
        >
          Archive all
        </Button>
      </div>
    </>
  );
}

function ArchivedNotifications({
  isLoading,
  notifications,
  onNavigate,
}: {
  isLoading: boolean;
  notifications: NotificationDto[];
  onNavigate: (notification: NotificationDto) => void;
}) {
  if (isLoading) {
    return <NotificationListSkeleton />;
  }

  if (notifications.length === 0) {
    return <EmptyNotifications archived />;
  }

  return (
    <ScrollArea className="h-[380px]">
      <div className="divide-y">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </ScrollArea>
  );
}

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const router = useRouter();
  const { status } = useStableSession();
  const {
    archiveAllNotifications,
    archiveNotification,
    archivedNotifications,
    hasUnreadNotifications,
    isNotificationsLoading,
    markAllNotificationsAsRead,
    markNotificationAsRead,
    notifications,
  } = useNotifications();

  if (status !== "authenticated") {
    return null;
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen && hasUnreadNotifications) {
      markAllNotificationsAsRead();
    }
  };

  const handleNavigate = (notification: NotificationDto) => {
    if (notification.status === "unread") {
      markNotificationAsRead(notification.id);
    }
    setOpen(false);
    if (notification.links?.href) {
      router.push(notification.links.href);
    }
  };

  return (
    <Popover onOpenChange={handleOpenChange} open={open}>
      <PopoverTrigger asChild>
        <Button
          aria-label="Notifications"
          className="relative size-8 rounded-full"
          size="icon"
          title="Notifications"
          type="button"
          variant="outline"
        >
          <Bell className="size-4" />
          {hasUnreadNotifications && (
            <span className="-right-0.5 -top-0.5 absolute size-2 rounded-full bg-[#FFD02B] ring-2 ring-background" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[calc(100vw-1rem)] max-w-[420px] p-0"
        sideOffset={10}
      >
        <Tabs defaultValue="inbox">
          <div className="flex items-center justify-between border-b px-2 py-2">
            <TabsList className="h-9">
              <TabsTrigger value="inbox">Inbox</TabsTrigger>
              <TabsTrigger value="archive">Archive</TabsTrigger>
            </TabsList>
            <Button
              asChild
              className="size-8 rounded-full"
              size="icon"
              variant="ghost"
            >
              <Link
                aria-label="Notification settings"
                href={accountPath({ tab: "notifications" })}
                onClick={() => setOpen(false)}
              >
                <Settings className="size-4" />
              </Link>
            </Button>
          </div>

          <TabsContent className="m-0" value="inbox">
            <InboxNotifications
              archiveAllNotifications={archiveAllNotifications}
              archiveNotification={archiveNotification}
              isLoading={isNotificationsLoading}
              notifications={notifications}
              onNavigate={handleNavigate}
            />
          </TabsContent>

          <TabsContent className="m-0" value="archive">
            <ArchivedNotifications
              isLoading={isNotificationsLoading}
              notifications={archivedNotifications}
              onNavigate={handleNavigate}
            />
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
